import { Injectable } from "@angular/core"
import { PersonDTO } from "@app/pages/person/person.service"
import {
  DistributionDTO,
  DistributionPerfDTO,
  PerformanceDTO,
  PerformanceService,
} from "@app/pages/performance/peformance.service"
import {
  ProgramConfigurationDTO,
  ProgramDTO,
  ProgramService,
  ProgramVersionDTO,
  UnitProgramDTO,
} from "@app/pages/program/program.service"
import { UnitDTO, UnitRoleDTO, UnitVersionDTO } from "@app/pages/unit/unit.service"
import { PartDTO, PartService, PartVersionDTO, partVersionPlaces, PlaceDTO } from "@app/pages/part/part.service"
import { REFERENTIAL, ReferentialDTO, ReferentialService } from "@app/pages/referential/referential.service"
import { GenericDropDownView } from "@shared/component/dropdown-ref-firestore/dropdown-ref-firestore.component"
import { ProjectService } from "@app/pages/performance/project.service"

export interface DetailsDistrib {
  role: Array<{
    personId: string
    personName: string
  }>
  roleId?: string
  substitutes: Array<{
    personId: string
    personName: string
  }>
  roleName: string
}

@Injectable({
  providedIn: "root",
})
export class DistributionService {
  constructor(
    private performanceService: PerformanceService,
    private refService: ReferentialService,
    private programService: ProgramService,
    private projectService: ProjectService,
    private partService: PartService
  ) {}

  // legacy : rôles définis directement dans les versions de pièces (UnitVersionDTO.roles)
  getRolesUnitsMap(programVersion?: Array<ProgramVersionDTO>): { [roleId: string]: UnitDTO } {
    const roleMap = {}
    programVersion?.forEach((x) =>
      x.units?.forEach((y) =>
        y.value.versions?.forEach((z) =>
          z.roles?.forEach((role) => {
            roleMap[role.id] = y.value
          })
        )
      )
    )
    return roleMap
  }

  // legacy
  getRolesMap(programVersion?: Array<ProgramVersionDTO>): { [roleId: string]: UnitRoleDTO } {
    const map = {}
    programVersion?.forEach((x) =>
      x.units?.forEach((y) =>
        y.value.versions?.forEach((z) =>
          z.roles?.forEach((role) => {
            map[role.id] = role
          })
        )
      )
    )
    return map
  }

  getUnitDistributionKey(unitId: string, programConfiguration?: ProgramConfigurationDTO): string {
    return `${unitId}##${this.getUnitVersionIdFromConfig(unitId, programConfiguration)}`
  }

  getDistribution(
    personId: string,
    unitId: string,
    distribution?: DistributionPerfDTO,
    programConfiguration?: ProgramConfigurationDTO
  ): DistributionDTO | undefined {
    const obj = (distribution && distribution[this.getUnitDistributionKey(unitId, programConfiguration)]) || {}
    return obj[personId]
  }

  getUnitVersionIdFromConfig(unitId: string, programConfiguration?: ProgramConfigurationDTO): string {
    const conf = programConfiguration?.unitsConf
    if (conf) {
      return conf[unitId]
    }
    return ""
  }

  // partsById : nécessaire pour compter les points des rôles/places des parties (nouvelle distribution).
  // Sans lui, seuls les rôles legacy des pièces sont comptés.
  getTotalPoints(
    person: PersonDTO,
    performance?: PerformanceDTO,
    program?: ProgramDTO,
    rolesMap?: { [roleId: string]: UnitRoleDTO },
    partsById?: { [partId: string]: PartDTO }
  ): { pts: number; ptsSub: number } {
    const rolesMap2 = rolesMap || this.getRolesMap(program?.versions)

    const distribution = performance?.distribution
    const programVersion = program?.versions?.find((x) => x.id === performance?.program_version_id)
    const programConfiguration = this.getConfiguration(performance, programVersion)

    // pas de version de programme => undefined (comportement historique)
    return programVersion?.units
      ?.map((unit) => ({
        unit,
        distrib: this.getDistribution(person.id, unit?.id || "", distribution, programConfiguration),
      }))
      .filter((d) => d.distrib != undefined)
      .reduce(
        (acc, { unit, distrib }) => {
          // legacy : rôles des pièces (points fixes)
          const points = distrib?.roles?.reduce((acc, r) => {
            const role = rolesMap2[r.id]
            return acc + (role ? +role.points : 0)
          }, 0)
          acc.pts += points || 0

          const points2 = distrib?.substitutes?.reduce((acc, r) => {
            const role = rolesMap2[r.id]
            return acc + (role ? +role.points * (r.coef || 1) : 0)
          }, 0)
          acc.ptsSub += points2 || 0

          // nouvelle distribution : rôles/places des parties (versions de parties et de points de la configuration)
          if (partsById) {
            const partRolesMap = this.getUnitPartRolesMap(unit, partsById, programConfiguration)
            distrib?.partRoles?.forEach((r) => {
              const role = partRolesMap[r.id]
              if (role) acc.pts += this.getPartRolePoints(unit.id, role, programConfiguration)
            })
            distrib?.partSubstitutes?.forEach((r) => {
              const role = partRolesMap[r.id]
              if (role) acc.ptsSub += this.getPartRolePoints(unit.id, role, programConfiguration) * (r.coef || 1)
            })
          }
          return acc
        },
        {
          pts: 0,
          ptsSub: 0,
        }
      ) as { pts: number; ptsSub: number }
  }

  getPersonDistribution(
    personId: string,
    performance: PerformanceDTO,
    onlyPieces = false
  ): Promise<{
    role: Array<{ unitId: string; unit: string; role: string; roleId: string }>
    substitute: Array<{ unitId: string; unit: string; role: string; roleId: string }>
  }> {
    const emptyResult = Promise.resolve({ role: [], substitute: [] })
    const distribution = performance?.distribution
    const programId = performance?.program?.id
    if (!programId || !distribution) {
      return emptyResult
    }
    let refUnits: Array<ReferentialDTO> = []
    let partsById: { [partId: string]: PartDTO } = {}
    return Promise.all([this.refService.getAllFromCache(REFERENTIAL.unity_type), this.getPartsById()]).then((res) => {
      refUnits = res[0]
      partsById = res[1]
      return this.programService.get(programId, true).then((program) => {
        const programVersion = program?.versions?.find((x) => x.id === performance?.program_version_id)

        const programConfiguration = this.getConfiguration(performance, programVersion)

        if (programVersion && programConfiguration) {
          const roleMap = this.getRolesMap([programVersion])
          const roleUnitMap = this.getRolesUnitsMap([programVersion])
          return programVersion?.units
            ?.map((unit) => {
              const d = this.getDistribution(personId, unit?.id || "", distribution, programConfiguration)
              return d && ({ ...d, unitId: unit?.id } as any)
            })
            .filter((d) => d != undefined)
            .reduce(
              (acc, distrib) => {
                // legacy : rôles des pièces
                distrib.roles?.forEach((r: any) => {
                  const id = r.id
                  const unit = roleUnitMap[id]
                  const refUnit = refUnits.find((x) => x?.id === unit?.type?.id) as any
                  if (refUnit?.visible && (!onlyPieces || (onlyPieces && refUnit.id === "PIECE")) /*FIXME*/) {
                    acc.role.push({
                      unitId: unit?.id,
                      unit: unit?.name,
                      role: roleMap[id]?.name,
                      roleId: id,
                    })
                  }
                })
                distrib.substitutes?.forEach((r: any) => {
                  const id = r.id
                  const unit = roleUnitMap[id]
                  const refUnit = refUnits.find((x) => x?.id === unit?.type?.id) as any
                  if (refUnit?.visible && (!onlyPieces || (onlyPieces && refUnit.id === "PIECE")) /*FIXME*/) {
                    acc.substitute.push({
                      unitId: unit?.id,
                      unit: unit?.name,
                      role: roleMap[id]?.name,
                      roleId: id,
                    })
                  }
                })

                // nouvelle distribution : rôles/places des parties
                const unitProgram = programVersion.units?.find((u) => u.id === distrib.unitId)
                const partUnit = unitProgram?.value
                const partRefUnit = refUnits.find((x) => x?.id === partUnit?.type?.id) as any
                if (unitProgram && partRefUnit?.visible && (!onlyPieces || partRefUnit.id === "PIECE") /*FIXME*/) {
                  const partRolesMap = this.getUnitPartRolesMap(unitProgram, partsById, programConfiguration)
                  const addPartRoles = (list: Array<{ id: string }> | undefined, target: Array<any>) =>
                    list?.forEach((r) => {
                      const role = partRolesMap[r.id]
                      if (role)
                        target.push({ unitId: partUnit?.id, unit: partUnit?.name, role: role.name, roleId: r.id })
                    })
                  addPartRoles(distrib.partRoles, acc.role)
                  addPartRoles(distrib.partSubstitutes, acc.substitute)
                }
                return acc
              },
              {
                role: [],
                substitute: [],
              }
            )
        }
        return emptyResult
      })
    })
  }

  // returns the configuration v1 if exist (performance.program_configuration_id) or configuration v2 otherwise (performance.configurationV2)
  getConfiguration(performance?: PerformanceDTO, programVersion?: ProgramVersionDTO) {
    let programConfiguration: ProgramConfigurationDTO | undefined = performance?.configurationV2
    if (performance?.program_configuration_id)
      programConfiguration = programVersion?.configurations?.find((x) => x.id === performance?.program_configuration_id)
    return programConfiguration
  }

  getDistributionDetails(
    performance: PerformanceDTO,
    personsFirstNameMap: { [personId: string]: string }
  ): Promise<
    Array<{
      unitId: string
      unitType: string
      unitName: string
      details: Array<DetailsDistrib>
    }>
  > {
    const emptyResult = Promise.resolve([])
    const distribution = performance?.distribution || {}
    const programId = performance?.program?.id
    if (!programId) {
      return emptyResult
    }
    let refUnits: Array<ReferentialDTO> = []
    let partsById: { [partId: string]: PartDTO } = {}
    return Promise.all([this.refService.getAllFromCache(REFERENTIAL.unity_type), this.getPartsById()]).then((res) => {
      refUnits = res[0]
      partsById = res[1]
      return this.programService.get(programId, false /*not working otherwise*/).then((program) => {
        const programVersion = program?.versions?.find((x) => x.id === performance?.program_version_id)
        const programConfiguration = this.getConfiguration(performance, programVersion)

        if (programVersion && programConfiguration) {
          const rolesMap = this.getRolesMap([programVersion])
          return programVersion?.units?.map((unit) => {
            const d = distribution[this.getUnitDistributionKey(unit?.id, programConfiguration)] || {}
            const partRoles = this.getUnitPartRoles(unit, partsById, programConfiguration)
            const partRolesMap = this.getUnitPartRolesMap(unit, partsById, programConfiguration)

            let results: Array<DetailsDistrib> = []
            const roleIds = {}
            const mapRoles = {}

            Object.keys(d).forEach((personId) => {
              const obj = d[personId]
              const firstName = personsFirstNameMap[personId] || ""
              const affectMapRole = (roleId: string, isSubstitute: boolean, roleName?: string) => {
                mapRoles[roleId] = mapRoles[roleId] || {
                  roleName: roleName ?? rolesMap[roleId]?.name,
                  role: [],
                  roleId: roleId,
                  substitutes: [],
                }
                if (isSubstitute) {
                  mapRoles[roleId].substitutes.push({
                    personName: firstName,
                    personId,
                  })
                } else {
                  mapRoles[roleId].role.push({
                    personName: firstName,
                    personId,
                  })
                }
                roleIds[roleId] = true
              }

              // legacy : rôles des pièces
              obj.roles?.forEach((role) => affectMapRole(role.id, false))
              obj.substitutes?.forEach((role) => affectMapRole(role.id, true))
              // nouvelle distribution : rôles/places des parties (versions sélectionnées uniquement)
              obj.partRoles?.forEach((role) => {
                if (partRolesMap[role.id]) affectMapRole(role.id, false, partRolesMap[role.id].name)
              })
              obj.partSubstitutes?.forEach((role) => {
                if (partRolesMap[role.id]) affectMapRole(role.id, true, partRolesMap[role.id].name)
              })
            })
            results = Object.values(mapRoles)

            const uniVersion = unit.value?.versions?.find((x) => x.id === programConfiguration?.unitsConf[unit.id])
            // legacy (uniVersion.roles) puis nouvelle distribution (rôles/places des parties)
            const roles2: Array<{ id: string; name: string }> = [...(uniVersion?.roles || []), ...partRoles]
            if (uniVersion) {
              roles2.forEach((role) => {
                if (!roleIds[role.id])
                  // if a role is not distributed, personName is empty
                  results.push({
                    role: [
                      {
                        personName: "",
                        personId: "",
                      },
                    ],
                    roleName: role.name,
                    roleId: role.id,
                    substitutes: [],
                  })
              })
            }

            // Sorting with the same order as typed in (important for displaying external view)
            results.sort((a, b) => {
              return roles2.findIndex((r) => r?.id === a?.roleId) - roles2.findIndex((r) => r?.id === b?.roleId)
            })

            return {
              unitId: unit?.value?.id,
              unitName: unit?.value?.name,
              details: results,
              unitType: unit?.value?.type?.id || "",
            }
          })
        }
        return emptyResult
      })
    })
  }

  // ---------- Nouvelle distribution : rôles/places des parties (PartDTO) ----------

  getPartsById(): Promise<{ [partId: string]: PartDTO }> {
    return this.partService
      .getAllFromCache()
      .then((parts) => {
        const map = {}
        parts?.forEach((part) => (map[part.id] = part))
        return map
      })
      .catch(() => ({}))
  }

  getUnitVersion(unit?: UnitProgramDTO, programConfiguration?: ProgramConfigurationDTO): UnitVersionDTO | undefined {
    const versionId = this.getUnitVersionIdFromConfig(unit?.id || "", programConfiguration)
    return unit?.value?.versions?.find((x) => x.id === versionId)
  }

  // version de la partie choisie dans la configuration, sinon sa version par défaut
  getPartVersionId(
    unitId: string,
    partId: string,
    partsById: { [partId: string]: PartDTO },
    programConfiguration?: ProgramConfigurationDTO
  ): string {
    const part = partsById[partId]
    const confVersionId = programConfiguration?.partsConf?.[unitId]?.[partId]
    const version =
      part?.versions?.find((v) => v.id === confVersionId) ||
      part?.versions?.find((v) => v.id === part.versionIdDefault) ||
      part?.versions?.[0]
    return version?.id || ""
  }

  getPartVersion(
    unitId: string,
    partId: string,
    partsById: { [partId: string]: PartDTO },
    programConfiguration?: ProgramConfigurationDTO
  ): PartVersionDTO | undefined {
    const versionId = this.getPartVersionId(unitId, partId, partsById, programConfiguration)
    return partsById[partId]?.versions?.find((v) => v.id === versionId)
  }

  // rôles/places des versions (sélectionnées) de toutes les parties de la pièce, dans l'ordre d'affichage
  getUnitPartRoles(
    unit: UnitProgramDTO | undefined,
    partsById: { [partId: string]: PartDTO },
    programConfiguration?: ProgramConfigurationDTO
  ): Array<PlaceDTO> {
    if (!unit) return []
    return (this.getUnitVersion(unit, programConfiguration)?.parts || []).reduce(
      (acc, part) =>
        acc.concat(
          partVersionPlaces(partsById[part.id], this.getPartVersion(unit.id, part.id, partsById, programConfiguration))
        ),
      [] as Array<PlaceDTO>
    )
  }

  getUnitPartRolesMap(
    unit: UnitProgramDTO | undefined,
    partsById: { [partId: string]: PartDTO },
    programConfiguration?: ProgramConfigurationDTO
  ): { [roleId: string]: PlaceDTO } {
    const map = {}
    this.getUnitPartRoles(unit, partsById, programConfiguration).forEach((role) => (map[role.id] = role))
    return map
  }

  // version de points choisie dans la configuration, sinon la version par défaut du rôle/place
  getPartRolePointId(unitId: string, role?: PlaceDTO, programConfiguration?: ProgramConfigurationDTO): string {
    const points = Array.isArray(role?.points) ? role?.points || [] : []
    const confPointId = programConfiguration?.partPointsConf?.[unitId]?.[role?.id || ""]
    const point =
      points.find((p) => p.id === confPointId) || points.find((p) => p.id === role?.pointIdDefault) || points[0]
    return point?.id || ""
  }

  getPartRolePoints(unitId: string, role?: PlaceDTO, programConfiguration?: ProgramConfigurationDTO): number {
    const pointId = this.getPartRolePointId(unitId, role, programConfiguration)
    const point = Array.isArray(role?.points) ? role?.points.find((p) => p.id === pointId) : undefined
    return +(point?.points || 0)
  }

  importDistribution(
    currentPerformance: PerformanceDTO,
    performanceToImport: PerformanceDTO,
    personToImportChoices: { [personId: string]: GenericDropDownView }
  ): Promise<void> {
    if (!currentPerformance || !performanceToImport) throw new Error("importDistribution, wrong parameters")

    return this.programService.get(currentPerformance.program?.id || "").then((program) => {
      currentPerformance.distribution = currentPerformance?.distribution || {}
      const distrib = currentPerformance.distribution

      performanceToImport.distribution = performanceToImport?.distribution || {}
      const distribToImport = performanceToImport?.distribution

      const programVersion = program.versions?.find((x) => x.id === currentPerformance.program_version_id)

      const programConf = this.getConfiguration(currentPerformance, programVersion)
      const programConfToImport = this.getConfiguration(performanceToImport, programVersion)

      const units = programVersion?.units || []
      return this.projectService.getPersonDistribution(currentPerformance).then((currentPersons) => {
        units.forEach((unit) => {
          const unitIdWithUnitVersionId = this.getUnitDistributionKey(unit.id, programConf)
          const unitIdWithUnitVersionIdToImport = this.getUnitDistributionKey(unit.id, programConfToImport)
          const distToImport = distribToImport[unitIdWithUnitVersionId] || {}
          currentPersons.forEach((person) => {
            const personIdToImport = personToImportChoices[person.id]?.id
            if (personIdToImport) {
              // if no choice, the distribution for this person should not be modified
              distrib[unitIdWithUnitVersionId] = distrib[unitIdWithUnitVersionId] || {}
              const d = distToImport[personIdToImport]
              if (d && unitIdWithUnitVersionId === unitIdWithUnitVersionIdToImport) {
                // if unit version in both current AND toImport conf
                distrib[unitIdWithUnitVersionId][person.id] = { ...d } // cloning distribution
              } else {
                delete distrib[unitIdWithUnitVersionId][person.id]
              }
            }
          })
        })
      })
    })
  }
}
